// @ts-nocheck
import { action, computed, makeObservable, observable } from 'mobx'
import { trackedAction } from 'mobx-async'
import { computedRouteParam } from '@stores/computedRouteParam'

import {
  CreateHomeServicesArgs,
  HomeServiceUpdateInputSettings,
  HomeServiceView,
  ServiceKind,
  ServiceView,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { Alert } from '@avantstay/backoffice-core'

import { arriere } from '@arriere/index'
import { getHomeServicesGrouped, updateHomeServices } from '@arriere/modules/homeServices'
import { ROUTES } from '@routes'

import type {
  EditVasProps,
  GroupedValueAddedService,
  StockedFridgePackageOptionView,
  ValueAddedService,
} from '../screens/propertyValueAddedServices/_types'

interface UpdateHomeServicesParams {
  serviceId: string
  enabled?: boolean
  price?: number
  settings?: HomeServiceUpdateInputSettings
}

class _PropertyValueAddedServicesStore {
  VASList?: GroupedValueAddedService[]

  homeId = computedRouteParam('id', {
    patterns: [ROUTES.propertyValueAddedServices.path],
  })

  constructor() {
    makeObservable(this, {
      VASList: observable,
      setVASList: action.bound,
      getServices: computed({ keepAlive: true }),
    })

    this.addNewService = trackedAction<any>(this.addNewService.bind(this))
    this.saveEditedVas = trackedAction<any>(this.saveEditedVas.bind(this))
    this.getServiceOptions = trackedAction<any>(this.getServiceOptions.bind(this))
    this.parseHomeServices = trackedAction<any>(this.parseHomeServices.bind(this))
    this.getHomeServices = trackedAction<any>(this.getHomeServices.bind(this))
  }

  setVASList(VAS: GroupedValueAddedService[] | undefined) {
    this.VASList = VAS
  }

  async getHomeServices(homeId: string) {
    const groupedServices = await getHomeServicesGrouped(homeId)

    return Promise.all(
      groupedServices.map(async group => ({
        title: group.title,
        homeServices: await this.parseHomeServices(homeId, group.homeServices),
      })),
    ).then(data => {
      this.setVASList(data)
      return data
    })
  }

  async parseHomeServices(
    homeId: string,
    homeServices: HomeServiceView[],
  ): Promise<ValueAddedService[]> {
    return Promise.all(
      homeServices.map(async service => ({
        ...(service.service || []),
        id: service.serviceId,
        enabled: service.enabled,
        priceInfo: {
          defaultPrice: service.service?.defaultPrice,
          price: service.price,
        },
        localSettings: service.localSettings,
        availableFrom: service.service?.availableFrom.sort(),
        serviceOptions: service.service
          ? await this.getServiceOptions(homeId, service.service)
          : undefined,
        hasValidService: !!service.service,
      })),
    )
  }

  async getServiceOptions(
    homeId: string,
    service: ServiceView,
  ): Promise<StockedFridgePackageOptionView[]> {
    if (service.kind === ServiceKind.stockedFridge) {
      return arriere.homeServices.getHomeServicesFridgeStockOptions(homeId, service.id)
    }

    return []
  }

  async saveEditedVas({
    serviceId,
    homeId,
    enabled,
    price,
    newNightlyRatio,
    newCleaningRatio,
  }: EditVasProps): Promise<void> {
    const mountParams = () => {
      const params: UpdateHomeServicesParams = {
        serviceId,
      }

      if (enabled !== undefined) {
        params.enabled = enabled
      }

      if (price !== undefined) {
        params.price = price
      }

      if (!!newNightlyRatio || !!newCleaningRatio) {
        params.settings = {
          newNightlyRatio,
          newCleaningRatio,
        }
      }

      return params
    }

    if (homeId) {
      return updateHomeServices({ homeId, ...mountParams() })
        .then(res => {
          if (!res) {
            Alert.Error('Error saving Value Added Service, please reload the page.')
          } else {
            Alert.Success('Saved!')
          }
        })
        .catch(err => Alert.Error(err))
    }
    return Promise.reject(new Error('Error saving Value Added Service: homeId was not provided'))
  }

  get getServices() {
    return arriere.homeServices
      .getServices()
      .then(services =>
        services.toSorted((serviceA, serviceB) => serviceA.name.localeCompare(serviceB.name)),
      )
  }

  async addNewService({ homeIds, input }: CreateHomeServicesArgs) {
    return arriere.homeServices
      .createHomeServices({ homeIds, input })
      .then(res => {
        if (!res) Alert.Error('Error adding new Value Added Service, please try again later!')
        else {
          Alert.Success('Added new Value Added Service!')
          this.getHomeServices(homeIds[0])
        }
      })
      .catch(err => Alert.Error(err))
  }
}

export const PropertyValueAddedServicesStore = new _PropertyValueAddedServicesStore()
